/**
 * Shared constants for the Search Results Map feature.
 *
 * @module searchResultsMap/constants
 */

/**
 * Minimum zoom level required for a rectangular-area search.
 *
 * Used in two places that must stay aligned:
 *
 *  1. `map.plugin` — sets the **initial zoom** when the map first loads with
 *     a `rectangularArea` location type. This ensures the map never opens
 *     below the constraint enforced during the session.
 *
 *  2. `searchWithinMap.plugin` — applies this min zoom whenever the location
 *     type transitions to `RECTANGULAR_AREA`, preventing the user from zooming
 *     out to a level where the bounding box would cover an impractically large area.
 *
 * If the value needs to change, update it here only.
 */
export const RECTANGULAR_AREA_MIN_ZOOM = 9;

/**
 * Default minimum zoom level used outside of a rectangular-area search.
 */
export const DEFAULT_MIN_ZOOM = 5;

/**
 * Maximum zoom level for the map, used to prevent users from zooming in too far and breaking the experience.
 */
export const DEFAULT_MAX_ZOOM = 20;

/**
 * Minimum zoom level at which site plans are rendered.
 * Must stay in sync with the MIN_ZOOM constant in
 * sitePlan.plugin and interactiveSitePlan.plugin.
 */
export const SITE_PLAN_RENDER_ZOOM_THRESHOLD = 14;

/**
 * Zoom level at which focused search activates.
 */
export const FOCUSED_SEARCH_ZOOM_THRESHOLD = 11;

/**
 * How far the viewport must drift from the backend-suggested bounds (from
 * mapBoundariesUpdated$ — the area covering the original search's farthest markers)
 * before autoSearchWithinMap.plugin activates a rectangular-area search. 0.8 means
 * the overlap between viewport and bounds must shrink to 20% of whichever of the two
 * is smaller (see services/geometry.js:getOutsideAreaRatio) — pure zoom in/out with
 * no panning never counts as drift, only heading into new, previously unseen territory does.
 *
 * Tunable — adjust this value only if the trigger feels too eager or too late.
 */
export const AUTO_SEARCH_WITHIN_MAP_OUTSIDE_RATIO_THRESHOLD = 0.8;

/**
 * Default zoom levels keyed by location type string.
 *
 * Used in two places that must stay aligned:
 *
 *  1. `map.plugin` — sets the **initial zoom** when the map is first created.
 *  2. `mapViewport.plugin` — resets the zoom whenever the location type changes
 *     (e.g. state → county) so the previous location type's zoom is not reused.
 *
 * If the value needs to change for any location type, update it here only.
 */
export const DEFAULT_ZOOM_LEVELS = {
  city: 11,
  county: 11,
  market: 9,
  state: 6,
  zip: 12,
  rectangularArea: RECTANGULAR_AREA_MIN_ZOOM,
  default: 12,
};
